import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getProspects, getProspectChildren, deleteProspect } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Building2, Plus, ChevronDown, ChevronRight, Phone, Mail, MapPin, Layers, Search, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Dedicated page for Syndics + their copropriétés.
 * Renders a 2-level hierarchical view:
 *   Syndic A
 *     ├ Copropriété 1
 *     ├ Copropriété 2
 *   Syndic B
 * Children (copropriétés) are loaded lazily when a syndic row is expanded.
 */
export const SyndicsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [syndics, setSyndics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState({}); // { [syndicId]: bool }
    const [childrenByParent, setChildrenByParent] = useState({}); // { [syndicId]: prospect[] }
    const [loadingChildren, setLoadingChildren] = useState({}); // { [syndicId]: bool }
    const [deleteTarget, setDeleteTarget] = useState(null); // syndic object being confirmed
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const res = await getProspects({ type_contact: 'syndic' });
                if (!cancelled) setSyndics(res.data || []);
            } catch {
                if (!cancelled) {
                    setSyndics([]);
                    toast.error('Erreur lors du chargement des syndics');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => {
        if (!search.trim()) return syndics;
        const q = search.trim().toLowerCase();
        return syndics.filter((s) =>
            [s.nom, s.prenom, s.societe, s.ville, s.email, s.telephone, s.siret]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q))
        );
    }, [syndics, search]);

    const toggleExpand = async (syndicId) => {
        const isOpen = !!expanded[syndicId];
        setExpanded((prev) => ({ ...prev, [syndicId]: !isOpen }));
        if (isOpen) return; // collapsing — nothing to fetch
        if (childrenByParent[syndicId]) return; // already loaded
        setLoadingChildren((prev) => ({ ...prev, [syndicId]: true }));
        try {
            const res = await getProspectChildren(syndicId);
            setChildrenByParent((prev) => ({ ...prev, [syndicId]: res.data || [] }));
        } catch {
            toast.error('Impossible de charger les copropriétés');
            setChildrenByParent((prev) => ({ ...prev, [syndicId]: [] }));
        } finally {
            setLoadingChildren((prev) => ({ ...prev, [syndicId]: false }));
        }
    };

    const totalCopros = useMemo(
        () => Object.values(childrenByParent).reduce((acc, arr) => acc + (arr?.length || 0), 0),
        [childrenByParent]
    );

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteProspect(deleteTarget.id);
            toast.success('Syndic supprimé');
            setSyndics((prev) => prev.filter((s) => s.id !== deleteTarget.id));
            setChildrenByParent((prev) => {
                const copy = { ...prev };
                delete copy[deleteTarget.id];
                return copy;
            });
            setDeleteTarget(null);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(detail || 'Erreur lors de la suppression');
        } finally {
            setDeleting(false);
        }
    };

    const deleteTargetLabel = deleteTarget
        ? (deleteTarget.societe || `${deleteTarget.prenom || ''} ${deleteTarget.nom || ''}`.trim() || 'ce syndic')
        : '';

    return (
        <div className="space-y-6" data-testid="syndics-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-[#0A0A0A] tracking-tight flex items-center gap-2">
                        <Building2 className="w-7 h-7 text-[#7C3AED]" /> Copropriétés &amp; Syndics
                    </h1>
                    <p className="text-[#52525B] mt-1 text-sm">
                        {syndics.length} syndic{syndics.length > 1 ? 's' : ''}
                        {totalCopros > 0 && ` · ${totalCopros} copropriété${totalCopros > 1 ? 's' : ''} chargée${totalCopros > 1 ? 's' : ''}`}
                    </p>
                </div>
                <Button
                    onClick={() => navigate('/prospects/new?type_contact=syndic')}
                    className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-sm h-11"
                    data-testid="add-syndic-btn"
                >
                    <Plus className="w-4 h-4 mr-2" /> Nouveau syndic
                </Button>
            </div>

            {/* Search */}
            <div className="bg-white border border-[#E4E4E7] p-4 rounded-sm">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                    <Input
                        placeholder="Rechercher un syndic (nom, société, SIRET, ville...)"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 h-11 rounded-sm"
                        data-testid="syndic-search-input"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#0A0A0A]"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="bg-white border border-[#E4E4E7] p-12 text-center text-[#52525B]">
                    Chargement des syndics...
                </div>
            ) : filtered.length === 0 ? (
                <div className="bg-white border border-dashed border-[#E4E4E7] p-12 text-center" data-testid="syndic-empty-state">
                    <Building2 className="w-12 h-12 mx-auto text-[#A1A1AA] mb-3" />
                    <p className="text-[#0A0A0A] font-medium">
                        {search ? 'Aucun syndic ne correspond à votre recherche.' : 'Aucun syndic enregistré.'}
                    </p>
                    {!search && (
                        <Button
                            onClick={() => navigate('/prospects/new?type_contact=syndic')}
                            className="mt-4 bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-sm"
                        >
                            <Plus className="w-4 h-4 mr-1" /> Créer le premier syndic
                        </Button>
                    )}
                </div>
            ) : (
                <ul className="space-y-3">
                    {filtered.map((s) => {
                        const isOpen = !!expanded[s.id];
                        const kids = childrenByParent[s.id] || [];
                        const isLoadingKids = !!loadingChildren[s.id];
                        return (
                            <li key={s.id} className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid={`syndic-card-${s.id}`}>
                                <div className="flex items-stretch">
                                    <button
                                        onClick={() => toggleExpand(s.id)}
                                        className="w-12 flex items-center justify-center hover:bg-[#FAFAFA] border-r border-[#E4E4E7] text-[#52525B]"
                                        title={isOpen ? 'Replier' : 'Déplier'}
                                        data-testid={`syndic-toggle-${s.id}`}
                                    >
                                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </button>
                                    <Link
                                        to={`/prospects/${s.id}`}
                                        className="flex-1 p-4 hover:bg-[#FAFAFA] flex flex-col sm:flex-row sm:items-center gap-3 min-w-0"
                                        data-testid={`syndic-link-${s.id}`}
                                    >
                                        <div className="w-12 h-12 rounded-full bg-[#7C3AED]/10 text-[#7C3AED] flex items-center justify-center shrink-0">
                                            <Building2 className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline gap-2 flex-wrap">
                                                <h3 className="font-semibold text-[#0A0A0A] truncate">
                                                    {s.societe || `${s.prenom || ''} ${s.nom || ''}`.trim() || 'Syndic sans nom'}
                                                </h3>
                                                {s.siret && (
                                                    <span className="text-xs text-[#52525B] font-mono">SIRET {s.siret}</span>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#52525B] mt-1">
                                                {s.telephone && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <Phone className="w-3 h-3" /> {s.telephone}
                                                    </span>
                                                )}
                                                {s.email && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <Mail className="w-3 h-3" /> {s.email}
                                                    </span>
                                                )}
                                                {(s.adresse || s.ville) && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <MapPin className="w-3 h-3" /> {[s.ville, s.code_postal].filter(Boolean).join(' ')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[#7C3AED]/10 text-[#7C3AED] text-xs font-semibold">
                                                <Layers className="w-3 h-3" /> {kids.length || 0} copro{(kids.length || 0) > 1 ? 's' : ''}
                                            </span>
                                            {s.commercial_name && (
                                                <p className="text-[11px] text-[#A1A1AA] mt-1">{s.commercial_name}</p>
                                            )}
                                        </div>
                                    </Link>
                                    {isAdmin && (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}
                                            className="px-3 flex items-center justify-center text-[#71717A] hover:text-[#DC2626] hover:bg-[#DC2626]/5 border-l border-[#E4E4E7]"
                                            title="Supprimer ce syndic"
                                            data-testid={`syndic-delete-btn-${s.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                                {isOpen && (
                                    <div className="border-t border-[#E4E4E7] bg-[#FAFAFA]/50">
                                        {isLoadingKids ? (
                                            <p className="p-4 text-sm text-[#52525B]">Chargement des copropriétés...</p>
                                        ) : kids.length === 0 ? (
                                            <div className="p-4 flex items-center justify-between">
                                                <p className="text-sm text-[#52525B] italic">Aucune copropriété rattachée à ce syndic.</p>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => navigate(`/prospects/new?parent_prospect_id=${s.id}`)}
                                                    className="rounded-sm"
                                                >
                                                    <Plus className="w-4 h-4 mr-1" /> Ajouter une copro
                                                </Button>
                                            </div>
                                        ) : (
                                            <>
                                                <ul className="divide-y divide-[#E4E4E7]">
                                                    {kids.map((c) => (
                                                        <li key={c.id} data-testid={`copro-row-${c.id}`}>
                                                            <Link
                                                                to={`/prospects/${c.id}`}
                                                                className="flex items-center gap-3 p-3 pl-12 hover:bg-white text-sm"
                                                            >
                                                                <div className="w-2 h-2 rounded-full bg-[#7C3AED]" />
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="font-medium text-[#0A0A0A] truncate">
                                                                        {c.nom_copropriete || c.societe || `${c.prenom || ''} ${c.nom || ''}`.trim()}
                                                                    </p>
                                                                    <p className="text-xs text-[#52525B]">
                                                                        {[c.adresse, c.code_postal, c.ville].filter(Boolean).join(' · ')}
                                                                    </p>
                                                                </div>
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <div className="p-3 pl-12 border-t border-[#E4E4E7] bg-white">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => navigate(`/prospects/new?parent_prospect_id=${s.id}`)}
                                                        className="rounded-sm"
                                                    >
                                                        <Plus className="w-4 h-4 mr-1" /> Ajouter une copro
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}>
                <AlertDialogContent data-testid="syndic-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-[#DC2626]" />
                            Supprimer ce syndic ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Vous êtes sur le point de supprimer <span className="font-semibold text-[#0A0A0A]">{deleteTargetLabel}</span>.
                            <br /><br />
                            La suppression sera bloquée si des copropriétés ou projets y sont encore rattachés.
                            Sinon, la fiche est déplacée dans la <span className="font-semibold text-[#DC2626]">Corbeille</span> et pourra être restaurée pendant <span className="font-semibold text-[#0A0A0A]">30 jours</span> depuis <code>/corbeille</code>.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting} data-testid="syndic-delete-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmDelete}
                            disabled={deleting}
                            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                            data-testid="syndic-delete-confirm"
                        >
                            {deleting ? 'Suppression…' : 'Déplacer dans la corbeille'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
